import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';
import { exhaustMap, filter, switchMap } from 'rxjs/operators';

import { selectIsBillingV3Company } from '../company/company-state.selectors';
import { SubscriptionActions } from '../company/subscription/subscription-state.actions';

import { EntitlementsStateActions } from './entitlements-state.actions';
import { EntitlementsService } from './entitlements.service';

@Injectable()
export class EntitlementsStateEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly entitlementsService = inject(EntitlementsService);

  /** The only bootstrap fetch. A second effect on fetchTier1Data ran before the company was known, ungated. */
  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntitlementsStateActions.init),
      switchMap(() => this.entitlementsService.isEntitlementsEnabled()),
      filter(isEntitlementsEnabled => isEntitlementsEnabled),
      exhaustMap(() =>
        this.entitlementsService
          .fetchEntitlementsToken()
          .pipe(map(() => EntitlementsStateActions.parseEntitlementsToken()))
      )
    )
  );

  clearEntitlements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntitlementsStateActions.clearEntitlements),
      map(() => {
        this.entitlementsService.clearEntitlementsToken();
        return EntitlementsStateActions.parseEntitlementsToken();
      })
    )
  );

  parseEntitlementsToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntitlementsStateActions.parseEntitlementsToken),
      map(() => {
        const entitlements = this.entitlementsService.parseEntitlementsToken();
        return EntitlementsStateActions.parseEntitlementsTokenSuccess({
          entitlements,
        });
      })
    )
  );

  handleGetUpdatedSubscriptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SubscriptionActions.getUpdatedSubscriptionSuccess,
        SubscriptionActions.getUpdatedSubscriptionAfterPlanChangeSuccess
      ),
      concatLatestFrom(() => this.store.select(selectIsBillingV3Company)),
      filter(([_, isBillingV3Company]) => isBillingV3Company === true),
      map(() => EntitlementsStateActions.init())
    )
  );
}
